import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AueTimeFrame } from '@cdw-ae/common-data';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { TailwindColors } from '../../../common/constants/tailwind-colors';
import { DevicesAutoUpdateExpirySummary } from '../../../layouts/internal/components/dashboard/dashboard.model';
import { BaseChartAbstract } from '../base-chart.class';
import { ChartCustomColor, ChartData } from '../models/chart.model';

@Component({
  selector: 'spog-pie-chart',
  templateUrl: './spog-pie-chart.component.html',
  styleUrls: ['./spog-pie-chart.component.scss'],
  imports: [NgxChartsModule, MatIconModule],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'flex w-full h-full min-h-[200px]',
  },
})
export class SpogPieChartComponent
  extends BaseChartAbstract
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('pieChart', { read: ElementRef }) pieChartRef!: ElementRef;

  // Pie chart specific inputs
  @Input() showCenterLabel = true;
  @Input() centerLabel = '0';
  @Input() centerLabelTemplate: TemplateRef<unknown> | null = null;
  @Input() totalValueFormatter: (value: number) => string = (value) =>
    value.toLocaleString();
  @Input() centerLabelForceShow = false;

  protected override initializeChart(
    dataSource: DevicesAutoUpdateExpirySummary[]
  ): ChartData[] {
    const aueSummary = dataSource[0] as DevicesAutoUpdateExpirySummary;

    const categoryCounts: Record<string, number> = {
      [AueTimeFrame.PastAUE]: aueSummary[AueTimeFrame.PastAUE] || 0,
      [AueTimeFrame.LessThan3Months]:
        aueSummary[AueTimeFrame.LessThan3Months] || 0,
      [AueTimeFrame.LessThan6Months]:
        aueSummary[AueTimeFrame.LessThan6Months] || 0,
      [AueTimeFrame.LessThan12Months]:
        aueSummary[AueTimeFrame.LessThan12Months] || 0,
      [AueTimeFrame.GreaterThan12Months]:
        aueSummary[AueTimeFrame.GreaterThan12Months] || 0,
      [AueTimeFrame.Unknown]: aueSummary[AueTimeFrame.Unknown] || 0,
    };

    return this.getCustomColors().map(({ name }) => ({
      name,
      value: categoryCounts[name] || 0,
    }));
  }

  // AUE donut slice colors (Figma-confirmed, EDUS-2590).
  override getCustomColors(): ChartCustomColor[] {
    return [
      { name: AueTimeFrame.PastAUE, value: TailwindColors.chartRed },
      {
        name: AueTimeFrame.LessThan3Months,
        value: TailwindColors.tertiaryOrangeDark,
      },
      { name: AueTimeFrame.LessThan6Months, value: TailwindColors.chartYellow },
      { name: AueTimeFrame.LessThan12Months, value: TailwindColors.chartGreen },
      {
        name: AueTimeFrame.GreaterThan12Months,
        value: TailwindColors.chartBlue,
      },
      { name: AueTimeFrame.Unknown, value: TailwindColors.gray200 },
    ];
  }

  // Pie chart specific properties
  totalValue = 0;
  formattedTotalValue = '';

  private resizeSubscription?: Subscription;
  private resizeDebounceTimer?: number;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.chartData) {
      this.onChartDataChange();
    }
  }

  override ngAfterViewInit() {
    // Small delay to ensure ngx-charts has rendered
    setTimeout(() => {
      this.addCenterLabel();
      this.setupResizeHandling();
    }, 100);
  }

  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
  }

  // Override base class method
  protected override onChartDataChange(): void {
    this.totalValue = this.calculateTotalValue();
    this.formattedTotalValue = this.totalValueFormatter(this.totalValue);
    this.centerLabel = this.totalValue.toString();
  }

  // Override base class method
  protected override onChartResize(): void {
    this.addCenterLabel();
  }

  private setupResizeHandling() {
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => {
        this.debouncedResize();
      });
  }

  private debouncedResize() {
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }

    this.resizeDebounceTimer = window.setTimeout(() => {
      this.onChartResize();
    }, 100);
  }

  private addCenterLabel() {
    if (!this.shouldShowCenterLabel()) {
      return;
    }

    const svgElement = this.getSvgElement();
    if (!svgElement) {
      return;
    }

    // Remove existing center label
    this.removeCenterLabel(svgElement);

    // Get the actual center of the pie chart
    const centerPoint = this.getPieChartCenter(svgElement);
    if (!centerPoint) {
      return;
    }

    // Create and add the center label
    const centerLabelGroup = this.createCenterLabelGroup(centerPoint);
    svgElement.appendChild(centerLabelGroup);
  }

  private shouldShowCenterLabel(): boolean {
    return this.showCenterLabel && (this.doughnut || this.centerLabelForceShow);
  }

  private getSvgElement(): SVGElement | null {
    const chartElement = this.pieChartRef?.nativeElement;
    return chartElement?.querySelector('svg') || null;
  }

  private removeCenterLabel(svgElement: SVGElement) {
    const existingLabel = svgElement.querySelector('.center-label-group');
    if (existingLabel) {
      existingLabel.remove();
    }
  }

  /**
   * Get the actual outer radius of the pie chart.
   * Uses the chart container dimensions since ngx-charts sizes the pie to fit.
   */
  private getChartOuterRadius(): number {
    const chartElement = this.pieChartRef?.nativeElement;
    if (!chartElement) {
      return 50; // Fallback
    }

    // Get the chart container dimensions
    const rect = chartElement.getBoundingClientRect();
    // ngx-charts pie chart uses the smaller dimension and adds some padding
    // The chart typically uses ~85% of the container
    const containerSize = Math.min(rect.width, rect.height);
    const outerRadius = (containerSize / 2) * 0.85;

    return outerRadius;
  }

  private getPieChartCenter(
    svgElement: SVGElement
  ): { x: number; y: number } | null {
    // First, try to find the pie chart group and get its transform
    const pieChartGroup = svgElement.querySelector('g.pie-chart');
    if (pieChartGroup) {
      const transform = pieChartGroup.getAttribute('transform');
      if (transform) {
        const translateMatch = transform.match(
          /translate\(([^,\s]+)[,\s]+([^)]+)\)/
        );
        if (translateMatch) {
          return {
            x: parseFloat(translateMatch[1]),
            y: parseFloat(translateMatch[2]),
          };
        }
      }
    }

    // Fallback: Calculate center based on SVG dimensions
    const svgRect = svgElement.getBoundingClientRect();
    //@ts-expect-error
    const svgViewBox = svgElement.viewBox.baseVal;

    // If viewBox is available, use it
    if (svgViewBox && svgViewBox.width > 0 && svgViewBox.height > 0) {
      return {
        x: svgViewBox.width / 2,
        y: svgViewBox.height / 2,
      };
    }

    // Final fallback: Use the actual SVG dimensions
    const svgWidth = svgElement.clientWidth || svgRect.width;
    const svgHeight = svgElement.clientHeight || svgRect.height;

    return {
      x: svgWidth / 2,
      y: svgHeight / 2,
    };
  }

  private createCenterLabelGroup(centerPoint: {
    x: number;
    y: number;
  }): SVGGElement {
    const centerLabelGroup = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'g'
    );
    centerLabelGroup.setAttribute('class', 'center-label-group');
    centerLabelGroup.setAttribute(
      'transform',
      `translate(${centerPoint.x}, ${centerPoint.y})`
    );
    centerLabelGroup.style.pointerEvents = 'none';

    // Get the actual outer radius from the chart container dimensions
    const outerRadius = this.getChartOuterRadius();
    // Calculate inner radius (donut hole) for responsive text sizing
    const innerRadius = outerRadius * (1 - this.arcWidth);
    const chartSizeFactor = innerRadius;

    if (this.formattedTotalValue) {
      const totalValueText = this.createTotalDevicesText(chartSizeFactor);
      centerLabelGroup.appendChild(totalValueText);
    }

    // Add center label text
    if (this.centerLabel && this.centerLabel !== '0') {
      const centerLabelText = this.createCenterLabelValueText(chartSizeFactor);
      centerLabelGroup.appendChild(centerLabelText);
    }

    return centerLabelGroup;
  }

  private createTotalDevicesText(chartSizeFactor: number): SVGTextElement {
    const totalValueText = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    totalValueText.setAttribute('text-anchor', 'middle');
    totalValueText.setAttribute('dominant-baseline', 'middle');
    totalValueText.setAttribute('dy', this.centerLabel ? '-1.2em' : '0');
    totalValueText.setAttribute('class', 'center-total-value');
    // "Total Devices" label — scales with inner radius, minimum 13px for readability
    const fontSize = Math.max(13, chartSizeFactor * 0.22);
    totalValueText.setAttribute('font-size', `${fontSize}px`);
    totalValueText.setAttribute('fill', TailwindColors.gray500);
    totalValueText.textContent = 'Total Devices';
    return totalValueText;
  }

  private createCenterLabelValueText(chartSizeFactor: number): SVGTextElement {
    const centerLabelText = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    centerLabelText.setAttribute('text-anchor', 'middle');
    centerLabelText.setAttribute('dominant-baseline', 'middle');
    centerLabelText.setAttribute('dy', '0.5em');
    centerLabelText.setAttribute('class', 'center-label-text');
    // Device count — scales with inner radius, minimum 20px for readability
    const fontSize = Math.max(20, chartSizeFactor * 0.4);
    centerLabelText.setAttribute('font-size', `${fontSize}px`);
    centerLabelText.setAttribute('fill', TailwindColors.gray400);
    centerLabelText.textContent = this.centerLabel;
    return centerLabelText;
  }

  getColorForItem(itemName: string): string {
    const colorItem = this.customColors.find(
      (color) => color.name === itemName
    );
    return colorItem ? colorItem.value : TailwindColors.primaryBlack;
  }
}
